import { environment } from '../../../environments/environment';
import { Magazine } from '../../core/models/magazine.model';
import { toImageSrc, toUploadApiUrl } from './image.util';

/** Encode each path segment so filenames with spaces work (`TS Sithirai 26.pdf`). */
export function encodeResourcePath(path: string): string {
  return path
    .split('/')
    .map((segment) => (segment ? encodeURIComponent(decodeURIComponent(segment)) : ''))
    .join('/');
}

function toAbsolutePdfUrl(raw: string): string | null {
  const url = raw.trim();
  if (!url) return null;

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      parsed.pathname = encodeResourcePath(parsed.pathname);
      return parsed.toString();
    } catch {
      return url;
    }
  }

  const looksPdf =
    url.toLowerCase().includes('.pdf') ||
    url.toLowerCase().includes('/upload/pdf/') ||
    url.toLowerCase().startsWith('upload/pdf/');

  if (!looksPdf) return null;

  const path = url.startsWith('/') ? url : `/${url}`;
  // Same /api/upload/... path used for images so nginx /api proxy serves wwwroot PDFs.
  return toUploadApiUrl(path);
}

/**
 * Candidate PDF URLs for a magazine (static upload path first, then API stream).
 * Callers should verify the response is actually a PDF before embedding.
 */
export function resolveMagazinePdfCandidates(
  magazine: Pick<Magazine, 'MagUrl' | 'MagDoc' | 'MagId'>
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();

  const push = (value: string | null | undefined) => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  };

  // Prefer API stream first — static MagUrl 404s often return the SPA HTML in an iframe.
  if (magazine.MagId > 0) {
    push(`${environment.apiBaseUrl}/Magazines/${magazine.MagId}/pdf`);
  }
  // MagUrl = PDF path; MagDoc = cover image path (do not treat MagDoc as PDF).
  push(toAbsolutePdfUrl(magazine.MagUrl || ''));

  return out;
}

/** Best-effort single URL for links / legacy callers. */
export function resolveMagazinePdfUrl(
  magazine: Pick<Magazine, 'MagUrl' | 'MagDoc' | 'MagId'>
): string | null {
  return resolveMagazinePdfCandidates(magazine)[0] ?? null;
}

export function magazineHasReader(magazine: Pick<Magazine, 'MagUrl' | 'MagDoc' | 'MagId'>): boolean {
  return resolveMagazinePdfCandidates(magazine).length > 0;
}

/** Cover image from MagDoc (preferred) or MagUrl when it is not a PDF path. */
export function magazineCoverSrc(
  magUrlOrMagazine?: string | null | Pick<Magazine, 'MagUrl' | 'MagDoc'>,
  magDoc?: string | null
): string | null {
  if (magUrlOrMagazine && typeof magUrlOrMagazine === 'object') {
    return firstImageSrc(magUrlOrMagazine.MagDoc) || firstImageSrc(magUrlOrMagazine.MagUrl);
  }

  return firstImageSrc(magDoc) || firstImageSrc(magUrlOrMagazine);
}

function firstImageSrc(raw?: string | null): string | null {
  const url = (raw || '').trim();
  if (!url || url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('/upload/pdf/')) {
    return null;
  }
  // Ignore legacy base64 MagDoc payloads — covers are short /upload/images/... paths.
  if (url.length > 500 || url.startsWith('data:')) {
    return null;
  }
  return toImageSrc(url);
}
